import { brusselsDateOf, formatEuros } from "@bivak/domain";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { StatusChip } from "../../components/beheer/StatusChip.js";
import { nl } from "../../copy/nl.js";
import { ApiError, fetchAdminBookings } from "../../lib/api.js";
import {
  type BookingGroups,
  type GroupedRow,
  groupBookings,
  type RowHint,
  verhuurtypeVan,
} from "../../lib/beheer.js";
import { asCents, formatDateRange } from "../../lib/format.js";

function hintText(hint: RowHint): string | null {
  switch (hint.kind) {
    case "nieuwe_aanvraag":
      return nl.beheer.hints.nieuweAanvraag;
    case "optie_vervalt":
      return nl.beheer.hints.optieVervalt(hint.dagen);
    case "af_te_rekenen":
      return nl.beheer.hints.afTeRekenen;
    case "verblijf_voorbij":
      return nl.beheer.hints.verblijfVoorbij;
    default:
      return null;
  }
}

/**
 * "weekendverhuur · 2 nachten" — the formula, not the space name: with one
 * building the space name on every row was pure noise.
 */
function typeRegel(row: GroupedRow): string {
  const type = verhuurtypeVan(row);
  const naam = type
    ? nl.kalender.types[type].naam.toLowerCase()
    : row.stayType === "day"
      ? nl.beheer.lijst.dag
      : nl.beheer.detail.overnachting;
  return row.stayType === "day" ? naam : `${naam} · ${nl.beheer.lijst.nachten(row.nights)}`;
}

function Row({ row }: { row: GroupedRow }) {
  const hint = hintText(row.hint);
  const urgent = row.hint.kind !== "geen";
  // Fixed tracks for dates, chip and price: rows are separate grids, and
  // auto-sized columns would resolve per row — nothing lines up vertically.
  // On a phone the same items reflow into two columns (name|price, then
  // dates|chip) via order — a fully stacked row was five lines and 150px of
  // scroll per booking (user feedback, 2026-08-21).
  return (
    <Link
      to="/beheer/boekingen/$boekingId"
      params={{ boekingId: row.id }}
      className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-lijn border-b px-4 py-2.5 last:border-b-0 hover:bg-zand/40 sm:grid-cols-[minmax(0,1fr)_11.5rem_9rem_5.5rem] sm:gap-x-4 sm:py-3"
    >
      <span className="order-1 min-w-0 sm:order-none">
        <span className="block truncate font-semibold text-[13.5px]">{row.partyName}</span>
        <span className="block truncate text-mist text-xs">
          {[
            row.purpose,
            row.personsBooked !== null ? nl.beheer.lijst.personen(row.personsBooked) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      <span className="order-3 min-w-0 font-mono text-[12.5px] sm:order-none">
        <span className="block truncate whitespace-nowrap">
          {formatDateRange(row.arrivalDate, row.departureDate)}
        </span>
        {/* The hint is the row's call to action: wrap, never truncate it. */}
        <span className={`block text-[11px] ${urgent ? "font-semibold text-amber" : "text-mist"}`}>
          {hint ?? typeRegel(row)}
        </span>
      </span>
      {/* Wrapper: a bare grid item stretches to the track, a pill must not. */}
      <span className="order-4 justify-self-end sm:order-none sm:justify-self-auto">
        <StatusChip
          status={row.status}
          label={row.isSystemParty ? nl.beheer.lijst.eigenWerking : undefined}
        />
      </span>
      <span className="order-2 justify-self-end font-mono text-[12.5px] sm:order-none">
        {row.totalCents === null ? "—" : formatEuros(asCents(row.totalCents))}
      </span>
    </Link>
  );
}

function Group({ label, rows }: { label: string; rows: GroupedRow[] }) {
  if (rows.length === 0) return null;
  return (
    <section className="mb-6">
      <h2 className="mb-2 font-mono font-semibold text-[11px] text-rood uppercase tracking-widest">
        {label} · {rows.length}
      </h2>
      <div className="overflow-hidden rounded-xl border border-lijn bg-kaart">
        {rows.map((row) => (
          <Row key={row.id} row={row} />
        ))}
      </div>
    </section>
  );
}

/** SCHERM 4 in the sketch: the volunteer's work queue, grouped by urgency. */
export function BeheerBoekingenPage() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: fetchAdminBookings,
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });

  if (isPending) return <p className="py-16 text-center text-mist">{nl.algemeen.laden}</p>;
  if (isError) {
    return (
      <div className="py-16 text-center">
        <p className="text-mist">{nl.algemeen.fout}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 min-h-11 rounded-lg bg-rood px-5 font-semibold text-kaart text-sm"
        >
          {nl.algemeen.opnieuwProberen}
        </button>
      </div>
    );
  }

  // "Vandaag" is the building's today (Europe/Brussels), same as everywhere
  // else — the UTC date is yesterday until 02:00 CEST and would keep a stay
  // that ended yesterday in "komende verblijven".
  const groups: BookingGroups = groupBookings(data.bookings, brusselsDateOf(new Date()).toString());
  const empty =
    groups.vraagtActie.length +
      groups.komend.length +
      groups.afgerond.length +
      groups.zonderGevolg.length ===
    0;

  return (
    <div>
      <h1 className="mb-5 font-bold font-display text-2xl tracking-tight">{nl.beheer.boekingen}</h1>
      {empty && <p className="text-mist">{nl.beheer.lijst.leeg}</p>}
      <Group label={nl.beheer.groepen.vraagtActie} rows={groups.vraagtActie} />
      <Group label={nl.beheer.groepen.komend} rows={groups.komend} />
      <Group label={nl.beheer.groepen.afgerond} rows={groups.afgerond} />
      <Group label={nl.beheer.groepen.zonderGevolg} rows={groups.zonderGevolg} />
    </div>
  );
}
